import { Link, NavLink, useLocation } from 'react-router-dom';
import { useWhiteLabel } from '../contexts/WhiteLabelContext';
import { hasAnyProfile, useAuth, useAuthActions } from '../contexts/AuthContext';

/** Item de navegação (rota + rótulo). */
interface ItemNav {
  readonly to: string;
  readonly rotulo: string;
  /** Ativo só na rota exata (ex.: a visão geral `/painel`, que é prefixo das outras). */
  readonly end?: boolean;
}

/** Sub-abas do Painel (M10): visão geral, mapa e qualidade. */
export const SUBNAV_PAINEL: readonly ItemNav[] = [
  { to: '/painel', rotulo: 'Visão geral', end: true },
  { to: '/painel/mapa', rotulo: 'Mapa' },
  { to: '/painel/qualidade', rotulo: 'Qualidade' },
];

/**
 * Sub-abas de Administração (só admin). A entrada "Administração" da topbar leva
 * à primeira; as demais telas ficam a um clique, na faixa de sub-abas.
 */
export const SUBNAV_ADMIN: readonly ItemNav[] = [
  { to: '/admin/usuarios', rotulo: 'Usuários' },
  { to: '/bases', rotulo: 'Bases' },
  { to: '/admin/lotes', rotulo: 'Lotes' },
  { to: '/reprocessamentos', rotulo: 'Reprocessamentos' },
  { to: '/admin/white-label', rotulo: 'White-label' },
  { to: '/admin/auditoria', rotulo: 'Auditoria' },
];

/**
 * Telas de administração fora das sub-abas: o reprocessamento avançado (formulário técnico) saiu do menu (ADR-036,
 * Req 8) e é aberto pelo link discreto da tela de Lotes; nela, "Administração" continua ativa.
 */
export const ADMIN_FORA_DO_MENU: readonly ItemNav[] = [{ to: '/admin/reprocessar', rotulo: 'Reprocessamento avançado' }];

function dentroDe(pathname: string, itens: readonly ItemNav[]): boolean {
  return itens.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
}

const classeAtiva = ({ isActive }: { isActive: boolean }): string => (isActive ? 'active' : '');

/**
 * AppShell — layout base do SPA: topbar (marca white-label + seções + usuário),
 * faixa de sub-abas quando a seção tem telas filhas (Painel, Administração) e a
 * área de conteúdo. Segue a topbar do protótipo e a spec do M11 (§12: "topbar com
 * todas as seções + Administração para admin"). Cargas e Administração só aparecem
 * para o grupo `admin` com MFA verificado (RN-M2-001, RN-GLOBAL-006, RN-M1-001) —
 * ocultação é defesa em profundidade; o backend valida o grupo. Sair encerra a
 * sessão (limpa tokens em memória).
 */
export function AppShell({ children }: { children?: React.ReactNode }): JSX.Element {
  const whiteLabel = useWhiteLabel();
  const auth = useAuth();
  const { logout } = useAuthActions();
  const { pathname } = useLocation();
  const ehAdmin = hasAnyProfile(auth, ['admin']) && auth.user?.mfaVerified === true;
  const naAdministracao = ehAdmin && dentroDe(pathname, [...SUBNAV_ADMIN, ...ADMIN_FORA_DO_MENU]);
  const subnav = pathname.startsWith('/painel') ? SUBNAV_PAINEL : naAdministracao ? SUBNAV_ADMIN : null;

  return (
    <div className="app-shell" style={{ ['--cor-primaria' as string]: whiteLabel.primaryColor }}>
      <header className="app-header">
        <div className="app-brand">
          {whiteLabel.logoUrl && (
            <img src={whiteLabel.logoUrl} alt="" className="app-logo" aria-hidden="true" />
          )}
          <h1 title={whiteLabel.systemName}>{whiteLabel.systemName}</h1>
        </div>
        <nav className="app-nav" aria-label="Seções">
          <NavLink to="/painel" className={classeAtiva}>
            Painel
          </NavLink>
          <NavLink to="/escolas" className={classeAtiva}>
            Escolas
          </NavLink>
          <NavLink to="/visitas" className={classeAtiva}>
            Visitas
          </NavLink>
          <NavLink to="/pastas" className={classeAtiva}>
            Pastas
          </NavLink>
          <NavLink to="/exportacoes" className={classeAtiva}>
            Exportações
          </NavLink>
          {ehAdmin && (
            <>
              <NavLink to="/cargas" className={classeAtiva}>
                Cargas
              </NavLink>
              {/* Link (não NavLink): ativo em QUALQUER tela da seção, não só na primeira. */}
              <Link
                to={SUBNAV_ADMIN[0].to}
                className={naAdministracao ? 'active' : ''}
                aria-current={naAdministracao ? 'page' : undefined}
              >
                Administração
              </Link>
            </>
          )}
        </nav>
        {auth.user && (
          <div className="app-user">
            {auth.user.email && (
              <span className="app-user-email" title={auth.user.email}>
                {auth.user.email}
              </span>
            )}
            <button type="button" className="btn app-sair" onClick={logout}>
              Sair
            </button>
          </div>
        )}
      </header>
      {subnav && (
        <nav className="app-subnav" aria-label="Telas da seção">
          <div className="app-subnav-inner">
            {subnav.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} className={classeAtiva}>
                {i.rotulo}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
      <main className="app-main">{children}</main>
    </div>
  );
}
